import type { BaseQueryApi, BaseQueryFn } from "@reduxjs/toolkit/query";
import { authClient, refreshAccessToken } from "@/api/auth-client";
import { getTokenExpiresIn, isTokenExpired } from "@/utils/jwt-utils";
import { clearCredentials, markAccessDenied, setCredentials } from "./auth.slice";

export interface GraphQLRequest {
    document: string;
    variables?: Record<string, unknown>;
    endpoint?: string;
    headers?: Record<string, string>;
    useDefaultApiKey?: boolean;
    signal?: AbortSignal;
}

export interface GraphQLError {
    message: string;
    errorType?: string;
    locations?: Array<{ line: number; column: number }>;
    path?: string[];
    extensions?: Record<string, unknown>;
}
const TOKEN_REFRESH_THRESHOLD_SECONDS = 60;
type GraphQLExecutionApi = Pick<BaseQueryApi, "dispatch" | "getState"> & Partial<Pick<BaseQueryApi, "signal">>;
const isAbortError = (error: unknown): boolean => (error instanceof Error || error instanceof DOMException) && error.name === "AbortError";

const shouldPreemptivelyRefresh = (token: string | null | undefined): token is string => {
    if (!token) {
        return false;
    }

    try {
        return getTokenExpiresIn(token) <= TOKEN_REFRESH_THRESHOLD_SECONDS;
    } catch {
        return false;
    }
};

export const executeGraphqlRequest = async (
    { document, variables, endpoint, headers: requestHeaders, useDefaultApiKey = true, signal }: GraphQLRequest,
    api: GraphQLExecutionApi,
): Promise<{ data: unknown } | { error: { errors: GraphQLError[] } }> => {
    const defaultGraphqlUrl = import.meta.env.VITE_GRAPHQL_ENDPOINT as string;
    const graphqlUrl = endpoint ?? defaultGraphqlUrl;
    const apiKey = import.meta.env.VITE_GRAPHQL_API_KEY as string | undefined;

    const buildHeaders = (accessToken?: string | null): Record<string, string> => {
        const headers: Record<string, string> = {
            "Content-Type": "application/json",
            ...(requestHeaders ?? {}),
        };

        if (useDefaultApiKey && apiKey && !headers["x-api-key"]) {
            headers["x-api-key"] = apiKey;
        }
        if (accessToken) {
            headers["Authorization"] = `Bearer ${accessToken}`;
        }

        return headers;
    };

    const isUnauthorizedGraphQLError = (errors?: GraphQLError[]): boolean => {
        if (!errors?.length) {
            return false;
        }

        return errors.some((error) => {
            const errorType = error.errorType?.toUpperCase();
            if (errorType === "UNAUTHORIZED" || errorType === "TOKENVALIDATIONERROR") {
                return true;
            }

            const code = String(error.extensions?.code ?? "").toUpperCase();
            if (code === "UNAUTHENTICATED" || code === "UNAUTHORIZED") {
                return true;
            }

            return /unauth|jwt|token.*(?:invalid|expired|malformed)/i.test(error.message);
        });
    };

    const isForbiddenGraphQLError = (errors?: GraphQLError[]): boolean => {
        if (!errors?.length) {
            return false;
        }

        return errors.some((error) => error.errorType?.toUpperCase() === "FORBIDDEN");
    };

    const request = async (allowRefresh: boolean): Promise<{ data: unknown } | { error: { errors: GraphQLError[] } }> => {
        const state = api.getState() as { auth?: { accessToken?: string | null } };
        const accessToken = authClient.getAccessToken() ?? state.auth?.accessToken;
        let tokenForRequest = accessToken;

        if (allowRefresh && shouldPreemptivelyRefresh(tokenForRequest)) {
            try {
                const refreshResponse = await refreshAccessToken();
                tokenForRequest = refreshResponse.accessToken;
                api.dispatch(setCredentials({ accessToken: tokenForRequest }));
            } catch (error) {
                if (isTokenExpired(tokenForRequest)) {
                    api.dispatch(clearCredentials());
                    return {
                        error: {
                            errors: [
                                {
                                    message: error instanceof Error ? error.message : "Authentication failed",
                                },
                            ],
                        },
                    };
                }
            }
        }

        let response: Response;
        try {
            response = await fetch(graphqlUrl, {
                method: "POST",
                headers: buildHeaders(tokenForRequest),
                body: JSON.stringify({ query: document, variables }),
                signal: signal ?? api.signal,
            });
        } catch (error) {
            if (isAbortError(error)) {
                return { error: { errors: [{ message: "Request aborted", errorType: "AbortError" }] } };
            }
            return {
                error: {
                    errors: [
                        {
                            message: error instanceof Error ? error.message : "Network error",
                        },
                    ],
                },
            };
        }

        let result: { data?: unknown; errors?: GraphQLError[] };
        try {
            result = (await response.json()) as { data?: unknown; errors?: GraphQLError[] };
        } catch (error) {
            if (isAbortError(error)) {
                return { error: { errors: [{ message: "Request aborted", errorType: "AbortError" }] } };
            }
            result = {};
        }

        const isForbidden = isForbiddenGraphQLError(result.errors);
        const isUnauthorized = !isForbidden && (response.status === 401 || isUnauthorizedGraphQLError(result.errors));

        // A forbidden response means the client's permissions are stale (route guards
        // let the user act, but the server denied it). Flag it so the app shows the
        // access-denied notice + countdown IN PLACE and AccessGuard signs the user out —
        // one screen change, no hard reload. This is the single centralised handler;
        // individual hooks / pages do NOT need to check for forbidden responses.
        if (isForbidden) {
            api.dispatch(markAccessDenied());
            return { error: { errors: result.errors ?? [] } };
        }

        // Only refresh if we actually sent a token. After logout the access token is cleared,
        // and the refresh cookie has been revoked server-side — attempting /v1/refresh would 400.
        if (allowRefresh && isUnauthorized && tokenForRequest) {
            try {
                const refreshResponse = await refreshAccessToken();
                api.dispatch(setCredentials({ accessToken: refreshResponse.accessToken }));
                return request(false);
            } catch (error) {
                api.dispatch(clearCredentials());
                return {
                    error: {
                        errors: [
                            {
                                message: error instanceof Error ? error.message : "Authentication failed",
                            },
                        ],
                    },
                };
            }
        }

        if (!allowRefresh && isUnauthorized) {
            api.dispatch(clearCredentials());
        }

        if (!response.ok) {
            return {
                error: {
                    errors: result.errors?.length
                        ? result.errors
                        : [
                              {
                                  message: `Request failed with status ${response.status}`,
                              },
                          ],
                },
            };
        }

        if (result.errors) {
            return { error: { errors: result.errors } };
        }

        return { data: result.data ?? null };
    };

    return request(true);
};

export const graphqlBaseQuery: BaseQueryFn<GraphQLRequest, unknown, { errors: GraphQLError[] }> = (request, api) => executeGraphqlRequest(request, api);
